import { useEffect, useMemo, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  MenuItem,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import MaybeTooltip from "../MaybeTooltip";
import {
  DEFAULT_WIND_MODELING,
  DENSITY_MODEL_OPTIONS,
  POWER_OUTPUT_MODEL_OPTIONS,
  PRESSURE_MODE_OPTIONS,
  SMOOTHING_ORDER_OPTIONS,
  STANDARD_DEVIATION_METHOD_OPTIONS,
  TEMPERATURE_MODEL_OPTIONS,
  WAKE_LOSSES_MODEL_OPTIONS,
  WIND_SPEED_MODEL_OPTIONS,
  isWindModelingModified,
} from "../../utils/windConfig";

const MODELING_TOOLTIPS = {
  windSpeedModel:
    "How windpowerlib extrapolates measured wind speed to hub height. " +
    "Hellman: power law v_hub = v x (h_hub / h_data)^a using the Hellman exponent. " +
    "Logarithmic: log wind profile using roughness length and obstacle height. " +
    "Interpolation / extrapolation (linear or logarithmic): fits between wind speeds at two " +
    "measurement heights (10 m plus the upper level, 80 m forecast / 100 m historical) - " +
    "fails if upper-level wind data is unavailable for the interval.",
  temperatureModel:
    "How air temperature at hub height is derived for the density calculation. " +
    "Linear gradient: T_hub = T_2m - 0.0065 K/m x height difference (standard lapse rate). " +
    "Interpolation / extrapolation needs temperature at two heights, which the platform " +
    "weather source does not provide - keep Linear gradient.",
  densityModel:
    "How air density at hub height is computed from temperature and pressure. " +
    "Ideal gas: rho = p_hub / (R_s x T_hub). Barometric: barometric height equation. " +
    "Interpolation / extrapolation needs density measured at two heights (not available " +
    "from the platform weather source). Only evaluated when density correction is on.",
  densityCorrection:
    "Shifts the power curve wind speeds to site air density " +
    "(v_corrected = v x (1.225 / rho)^n, IEC 61400-12-1) before reading off turbine output, " +
    "instead of assuming standard density 1.225 kg/m3. Recommended for high-altitude or hot sites.",
  hellmanExponent:
    "Power-law shear exponent a in v_hub = v x (h_hub / h_data)^a. " +
    "Typical: 0.14 (1/7, onshore neutral conditions), ~0.11 (offshore), 0.25+ (rough/stable). " +
    "Leave empty to derive it from terrain: a = 1 / ln(h_hub / z0). Used only by the Hellman model.",
  roughnessLength:
    "Terrain roughness length z0 in meters, the height where the log wind profile reaches zero. " +
    "~0.0002 open water, 0.03 open grassland, 0.1-0.25 farmland with hedges, 0.5-1+ forest or " +
    "urban. Used by the Logarithmic profile, and by Hellman when no exponent is set.",
  obstacleHeight:
    "Average height of obstacles (trees, buildings) around the turbine. The logarithmic profile " +
    "is shifted upward by d = 0.7 x obstacle height; 0.7 x obstacle height must stay below the " +
    "10 m wind measurement height. Set 0 for widespread low obstacles. Used only by the " +
    "Logarithmic model.",
  pressureMode:
    "Source of surface air pressure feeding the density model. Measured: surface pressure " +
    "from the weather data for the site. Elevation fallback: barometric estimate from site " +
    "measurements are unavailable ('Measured only' fails instead). Fixed: a constant you set.",
  fixedPressurePa:
    "Constant surface pressure in pascals used when pressure mode is Fixed " +
    "(standard atmosphere at sea level: 101325 Pa). The density model scales it to hub height.",
  powerOutputModel:
    "How turbine output is read from the turbine data. Power curve: P(v) directly from the " +
    "manufacturer curve (supports density correction and farm-level wake/smoothing). " +
    "Power coefficient curve: P = 1/2 x rho x pi/4 x D^2 x cp(v) x v^3 - computed per turbine, " +
    "requires a cp curve and rotor diameter in the catalog, and cannot be combined with " +
    "wake losses or smoothing.",
  wakeLossesModel:
    "How wake losses inside the farm are modeled (windpowerlib TurbineClusterModelChain). " +
    "Wind efficiency curves (dena-Netzstudie II / Knorr) reduce the hub-height wind speed as a " +
    "function of wind speed - 'mean' curves are averages over many German farms, 'extreme' " +
    "variants bound strongly deviating single farms. Constant farm efficiency instead scales " +
    "the farm power curve by the Farm efficiency %. None disables wake losses. With any curve " +
    "model, the Farm efficiency % is still applied on top as electrical/availability losses. " +
    "Wake losses are skipped automatically for single-turbine parks.",
  powerCurveSmoothing:
    "Smooths the aggregated power curve with a Gauss distribution to represent the spread of " +
    "wind speeds across the farm area. Yields more realistic energy estimates around rated " +
    "wind speed for multi-turbine parks (single turbine curves are unrealistically steep).",
  standardDeviationMethod:
    "How the Gauss standard deviation for smoothing is computed. Turbulence intensity: " +
    "sigma = v x TI (site-specific). Staffell-Pfenninger: sigma = 0.6 x (0.2 x v) " +
    "(empirical, Staffell & Pfenninger 2016).",
  smoothingOrder:
    "Whether smoothing is applied to the aggregated farm power curve (default) or to each " +
    "turbine power curve before aggregation.",
  turbulenceIntensity:
    "Turbulence intensity TI at hub height used for smoothing (dimensionless, e.g. 0.1-0.2). " +
    "Leave empty to derive it from terrain: TI = 1 / ln(hub height / roughness length).",
};

function countChangedSettings(modeling) {
  const normalized = {
    ...DEFAULT_WIND_MODELING,
    ...(modeling || {}),
  };

  return Object.keys(DEFAULT_WIND_MODELING).reduce((count, key) => {
    if (normalized[key] !== DEFAULT_WIND_MODELING[key]) {
      return count + 1;
    }
    return count;
  }, 0);
}

function FieldLabel({ label, tooltip }) {
  return (
    <Box
      component="span"
      sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
    >
      <Typography
        variant="subtitle2"
        component="span"
        sx={{
          fontWeight: 700,
          color: "primary.main",
          letterSpacing: "0.02em",
          fontSize: "1.15rem",
        }}
      >
        {label}
      </Typography>
      {tooltip ? (
        <MaybeTooltip
          title={tooltip}
          arrow
          placement="top"
          slotProps={{
            tooltip: {
              sx: {
                fontSize: "0.9rem",
                lineHeight: 1.45,
                maxWidth: 380,
                p: 1.25,
              },
            },
          }}
        >
          <InfoOutlinedIcon
            sx={{
              fontSize: "1.05rem",
              color: "primary.main",
              opacity: 0.75,
              cursor: "help",
              "&:hover": { opacity: 1 },
            }}
          />
        </MaybeTooltip>
      ) : null}
    </Box>
  );
}

function LabeledNumberField({
  label,
  value,
  onChange,
  tooltip,
  min,
  step = "any",
  sx,
}) {
  return (
    <TextField
      fullWidth
      size="small"
      type="number"
      label={<FieldLabel label={label} tooltip={tooltip} />}
      value={value}
      onChange={(event) =>
        onChange(event.target.value === "" ? "" : Number(event.target.value))
      }
      inputProps={{ min, step }}
      sx={sx}
      InputLabelProps={{ shrink: true }}
    />
  );
}

function LabeledSelectField({ label, value, onChange, tooltip, options, sx }) {
  return (
    <TextField
      fullWidth
      size="small"
      select
      label={<FieldLabel label={label} tooltip={tooltip} />}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      sx={sx}
      InputLabelProps={{ shrink: true }}
      SelectProps={{
        // The menu anchors below the field; a tight max height keeps it from
        // being clamped upward (over the field) when the browser window edge
        // is close - longer lists scroll inside the menu instead.
        MenuProps: {
          anchorOrigin: { vertical: "bottom", horizontal: "left" },
          transformOrigin: { vertical: "top", horizontal: "left" },
          marginThreshold: 8,
          PaperProps: { sx: { maxHeight: 240 } },
        },
      }}
    >
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );
}

function SettingsBlock({ title, children, sx }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.15, borderRadius: 2.25, ...sx }}>
      <Stack spacing={1}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        {children}
      </Stack>
    </Paper>
  );
}

export default function ModelingSettingsSection({ value, onChange, onReset }) {
  const [expanded, setExpanded] = useState(false);
  const modified = useMemo(() => isWindModelingModified(value), [value]);
  const changedCount = useMemo(() => countChangedSettings(value), [value]);
  const normalizedValue = useMemo(
    () => ({
      ...DEFAULT_WIND_MODELING,
      ...(value || {}),
    }),
    [value]
  );
  const isHellmanModel = normalizedValue.windSpeedModel === "hellman";
  const isLogModel =
    normalizedValue.windSpeedModel === "logarithmic" ||
    normalizedValue.windSpeedModel === "log_interpolation_extrapolation";

  const textFieldSx = {
    "& .MuiInputBase-root": {
      borderRadius: 1.35,
      backgroundColor: "background.paper",
    },
    "& .MuiInputBase-input": {
      py: 1.05,
      fontSize: "1.08rem",
      lineHeight: 1.35,
    },
    "& .MuiSelect-select": {
      fontSize: "1.08rem",
      lineHeight: 1.35,
    },
    "& .MuiInputLabel-root": {
      transform: "translate(14px, -9px) scale(0.75)",
      pointerEvents: "auto",
    },
  };

  useEffect(() => {
    if (modified) setExpanded(true);
  }, [modified]);

  const handleField = (field, fieldValue) => {
    onChange({
      ...DEFAULT_WIND_MODELING,
      ...(value || {}),
      [field]: fieldValue,
    });
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={(_event, nextExpanded) => setExpanded(nextExpanded)}
      disableGutters
      elevation={0}
      sx={{
        mt: 3,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2.25,
        overflow: "hidden",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 2,
          py: 0.75,
          bgcolor: modified ? "action.hover" : "background.paper",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          sx={{ width: "100%" }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Modeling Settings
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Defensible user-facing controls for wind profile, shear assumptions, and air
              pressure handling.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Box
              sx={{
                px: 1.1,
                py: 0.45,
                borderRadius: 999,
                border: "1px solid",
                borderColor: modified ? "primary.main" : "divider",
                bgcolor: (theme) =>
                  modified ? alpha(theme.palette.primary.main, 0.08) : "transparent",
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: modified ? "primary.main" : "text.secondary" }}
              >
                {modified ? `${changedCount} settings changed` : "Using defaults"}
              </Typography>
            </Box>
            <Button
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                onReset();
              }}
            >
              Restore defaults
            </Button>
          </Stack>
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 2, py: 1.25 }}>
        <Box
          sx={{
            display: "grid",
            gap: 1.25,
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              xl: "repeat(3, minmax(0, 1fr))",
              lg: "repeat(2, minmax(0, 1fr))",
            },
            alignItems: "start",
          }}
        >
          <SettingsBlock title="Model choice">
            <Stack spacing={1}>
              <LabeledSelectField
                label="Wind speed model"
                value={normalizedValue.windSpeedModel}
                onChange={(fieldValue) => handleField("windSpeedModel", fieldValue)}
                tooltip={MODELING_TOOLTIPS.windSpeedModel}
                options={WIND_SPEED_MODEL_OPTIONS}
                sx={textFieldSx}
              />
              <LabeledSelectField
                label="Density model"
                value={normalizedValue.densityModel}
                onChange={(fieldValue) => handleField("densityModel", fieldValue)}
                tooltip={MODELING_TOOLTIPS.densityModel}
                options={DENSITY_MODEL_OPTIONS}
                sx={textFieldSx}
              />
              <LabeledSelectField
                label="Temperature model"
                value={normalizedValue.temperatureModel}
                onChange={(fieldValue) => handleField("temperatureModel", fieldValue)}
                tooltip={MODELING_TOOLTIPS.temperatureModel}
                options={TEMPERATURE_MODEL_OPTIONS}
                sx={textFieldSx}
              />
              <LabeledSelectField
                label="Power output model"
                value={normalizedValue.powerOutputModel}
                onChange={(fieldValue) => handleField("powerOutputModel", fieldValue)}
                tooltip={MODELING_TOOLTIPS.powerOutputModel}
                options={POWER_OUTPUT_MODEL_OPTIONS}
                sx={textFieldSx}
              />
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 1.35,
                  px: 1.25,
                  py: 0.65,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Box>
                  <FieldLabel
                    label="Density correction"
                    tooltip={MODELING_TOOLTIPS.densityCorrection}
                  />
                  <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                    Adjust turbine output for air-density changes.
                  </Typography>
                </Box>
                <Switch
                  checked={Boolean(normalizedValue.densityCorrection)}
                  onChange={(event) => handleField("densityCorrection", event.target.checked)}
                />
              </Paper>
            </Stack>
          </SettingsBlock>

          <SettingsBlock title="Farm / wake effects">
            <Stack spacing={1}>
              <LabeledSelectField
                label="Wake losses model"
                value={normalizedValue.wakeLossesModel}
                onChange={(fieldValue) => handleField("wakeLossesModel", fieldValue)}
                tooltip={MODELING_TOOLTIPS.wakeLossesModel}
                options={WAKE_LOSSES_MODEL_OPTIONS}
                sx={textFieldSx}
              />

              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 1.35,
                  px: 1.25,
                  py: 0.65,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Box>
                  <FieldLabel
                    label="Power curve smoothing"
                    tooltip={MODELING_TOOLTIPS.powerCurveSmoothing}
                  />
                  <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                    Account for the spread of wind speeds across the farm.
                  </Typography>
                </Box>
                <Switch
                  checked={Boolean(normalizedValue.powerCurveSmoothing)}
                  onChange={(event) => handleField("powerCurveSmoothing", event.target.checked)}
                />
              </Paper>

              {normalizedValue.powerCurveSmoothing ? (
                <>
                  <LabeledSelectField
                    label="Std. deviation method"
                    value={normalizedValue.standardDeviationMethod}
                    onChange={(fieldValue) => handleField("standardDeviationMethod", fieldValue)}
                    tooltip={MODELING_TOOLTIPS.standardDeviationMethod}
                    options={STANDARD_DEVIATION_METHOD_OPTIONS}
                    sx={textFieldSx}
                  />
                  <LabeledSelectField
                    label="Smoothing applied to"
                    value={normalizedValue.smoothingOrder}
                    onChange={(fieldValue) => handleField("smoothingOrder", fieldValue)}
                    tooltip={MODELING_TOOLTIPS.smoothingOrder}
                    options={SMOOTHING_ORDER_OPTIONS}
                    sx={textFieldSx}
                  />
                  {normalizedValue.standardDeviationMethod === "turbulence_intensity" ? (
                    <LabeledNumberField
                      label="Turbulence intensity"
                      value={normalizedValue.turbulenceIntensity}
                      onChange={(fieldValue) => handleField("turbulenceIntensity", fieldValue)}
                      tooltip={MODELING_TOOLTIPS.turbulenceIntensity}
                      min={0}
                      step={0.01}
                      sx={textFieldSx}
                    />
                  ) : null}
                </>
              ) : null}
            </Stack>
          </SettingsBlock>

          <Stack spacing={1.25}>
          <SettingsBlock title="Site / shear assumptions">
            <Stack spacing={1}>
              {isHellmanModel ? (
                <LabeledNumberField
                  label="Hellman exponent"
                  value={normalizedValue.hellmanExponent}
                  onChange={(fieldValue) => handleField("hellmanExponent", fieldValue)}
                  tooltip={MODELING_TOOLTIPS.hellmanExponent}
                  sx={textFieldSx}
                />
              ) : null}

              <LabeledNumberField
                label="Roughness length (m)"
                value={normalizedValue.roughnessLength}
                onChange={(fieldValue) => handleField("roughnessLength", fieldValue)}
                tooltip={MODELING_TOOLTIPS.roughnessLength}
                min={0}
                sx={{
                  ...textFieldSx,
                  ...(isLogModel
                    ? {
                        "& .MuiInputBase-root": {
                          borderRadius: 1.35,
                          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.06),
                        },
                      }
                    : {}),
                }}
              />

              {isLogModel ? (
                <LabeledNumberField
                  label="Obstacle height (m)"
                  value={normalizedValue.obstacleHeight}
                  onChange={(fieldValue) => handleField("obstacleHeight", fieldValue)}
                  tooltip={MODELING_TOOLTIPS.obstacleHeight}
                  min={0}
                  sx={{
                    ...textFieldSx,
                    "& .MuiInputBase-root": {
                      borderRadius: 1.35,
                      backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.06),
                    },
                  }}
                />
              ) : null}
            </Stack>
          </SettingsBlock>

          <SettingsBlock title="Air / pressure">
            <Stack spacing={1}>
              <LabeledSelectField
                label="Pressure mode"
                value={normalizedValue.pressureMode}
                onChange={(fieldValue) => handleField("pressureMode", fieldValue)}
                tooltip={MODELING_TOOLTIPS.pressureMode}
                options={PRESSURE_MODE_OPTIONS}
                sx={textFieldSx}
              />

              {normalizedValue.pressureMode === "fixed" ? (
                <LabeledNumberField
                  label="Fixed pressure (Pa)"
                  value={normalizedValue.fixedPressurePa}
                  onChange={(fieldValue) => handleField("fixedPressurePa", fieldValue)}
                  tooltip={MODELING_TOOLTIPS.fixedPressurePa}
                  min={0}
                  sx={textFieldSx}
                />
              ) : null}
            </Stack>
          </SettingsBlock>
          </Stack>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
